import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import GlobalSearch from './GlobalSearch';
import NotificationCenter from './NotificationCenter';
import {
  LayoutDashboard,
  Users,
  ArrowLeftRight,
  AlertTriangle,
  FileText,
  Search,
  Shield,
  Database,
  Bitcoin,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  History,
  Command,
  TrendingUp,
  FileBarChart,
  FlaskConical,
  Palette,
  CalendarClock,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Analytics', href: '/analytics', icon: TrendingUp },
  { name: 'Entities', href: '/entities', icon: Users },
  { name: 'Transactions', href: '/transactions', icon: ArrowLeftRight },
  { name: 'Alerts', href: '/alerts', icon: AlertTriangle },
  { name: 'Cases', href: '/cases', icon: FileText },
  { name: 'Network Analysis', href: '/network', icon: Search },
  { name: 'Entity Screening', href: '/watchlists', icon: Shield },
  { name: 'Crypto Intel', href: '/crypto', icon: Bitcoin },
  { name: 'Data Sources', href: '/data-sources', icon: Database },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Report Builder', href: '/report-builder', icon: FileBarChart },
  { name: 'A/B Testing', href: '/ab-testing', icon: FlaskConical },
  { name: 'Scheduled Screening', href: '/scheduled-screening', icon: CalendarClock },
  { name: 'Audit Logs', href: '/audit-logs', icon: History },
];

const adminNavigation = [
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Default logo URL
const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_cyber-shield-84/artifacts/7e8nuixk_aegisFI%20Logo-004.png';

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/public`);
        const data = await response.json();
        if (data?.logo_url) {
          setLogoUrl(data.logo_url);
        }
      } catch (error) {
        // Use default logo
      }
    };
    loadBranding();
  }, []);

  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background grid-bg">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 sidebar transform transition-transform duration-300 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/5">
            <Link to="/" className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="AEGIS" className="h-8 w-auto" />
              ) : (
                <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
              )}
              <div>
                <h1 className="font-secondary text-sm font-bold uppercase tracking-wider text-white">
                  AEGIS
                </h1>
                <p className="text-[10px] font-mono text-muted-foreground tracking-widest">
                  FINANCIAL INTELLIGENCE
                </p>
              </div>
            </Link>
            <button
              className="lg:hidden text-muted-foreground hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm',
                    isActive
                      ? 'active bg-primary/10 text-primary border-l-2 border-primary'
                      : 'text-muted-foreground hover:text-white'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}

            {hasRole(['admin', 'supervisor']) && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/50">
                    Administration
                  </p>
                </div>
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm',
                        isActive
                          ? 'active bg-primary/10 text-primary border-l-2 border-primary'
                          : 'text-muted-foreground hover:text-white'
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <User className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.full_name}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button
              className="lg:hidden text-muted-foreground hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-3">
              {/* Global Search */}
              <Button
                variant="ghost"
                className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-white"
                onClick={() => setSearchOpen(true)}
                data-testid="global-search-btn"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Search</span>
                <kbd className="hidden md:inline-flex h-5 select-none items-center gap-1 rounded border border-white/10 bg-white/5 px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <Command className="w-3 h-3" /> K
                </kbd>
              </Button>
              
              {/* Notifications */}
              <NotificationCenter />

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-muted-foreground hover:text-white"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive focus:text-destructive"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default Layout;
